import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, LinearProgress, Stack, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { readPreparationClock } from "../../../services/signaturePreparation";
import type { PreparationProgress } from "../../../services/signaturePreparation";

/**
 * The wait while a contract is prepared: that the click was accepted (and
 * when), which step is running, and roughly how long is left — worded as an
 * estimate, and honest once it runs past it. The estimate itself comes from
 * production timings (`PREPARATION_TIMINGS`).
 */
export default function SignaturePreparationProgress({
  progress,
  questionnaire = false
}: {
  progress: PreparationProgress;
  /** A questionnaire on its own (שאלון בלבד) is being prepared: "the questionnaire", not "the contract". */
  questionnaire?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  // The `_questionnaire` variant of a line, where one exists (i18next context).
  const context = questionnaire ? "questionnaire" : undefined;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, []);
  const clock = readPreparationClock(progress, Math.max(now, progress.acceptedAt));
  const startedAt = new Date(progress.acceptedAt).toLocaleTimeString(i18n?.language === "en" ? "en-GB" : "he-IL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
  const title =
    progress.kind === "edit"
      ? t("signatures.newSignatureForm.preparation.titleEdit", { context })
      : progress.documentCount > 1
      ? t("signatures.newSignatureForm.preparation.titleBundle", { documents: progress.documentCount })
      : t("signatures.newSignatureForm.preparation.title", { context });
  const stepLabel =
    progress.step === "number"
      ? t("signatures.newSignatureForm.preparation.steps.number", { context })
      : progress.step === "save"
      ? t("signatures.newSignatureForm.preparation.steps.save", { context })
      : progress.documentCount > 1
      ? t("signatures.newSignatureForm.preparation.steps.compileBundle")
      : t("signatures.newSignatureForm.preparation.steps.compile");
  // A bundle counts its PDFs as they become ready, on the time line (the step
  // line stays one line at phone width).
  const readyCount =
    progress.step === "compile" && progress.documentCount > 1
      ? t("signatures.newSignatureForm.preparation.readyCount", { done: progress.compiledCount, total: progress.documentCount })
      : null;
  const timeLeft = clock.overdue
    ? t("signatures.newSignatureForm.preparation.overdue")
    : clock.secondsLeft !== null && clock.secondsLeft >= 4
    ? t("signatures.newSignatureForm.preparation.secondsLeft", { seconds: clock.secondsLeft })
    : t("signatures.newSignatureForm.preparation.fewSeconds");

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="signature-preparation-progress"
      sx={{
        flexShrink: 0,
        borderTop: 1,
        borderColor: "divider",
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
        px: { xs: 2, md: 3 },
        py: 1.25
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="subtitle2" component="p">
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          {t("signatures.newSignatureForm.preparation.startedAt", { time: startedAt })}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.25 }}>
        {t("signatures.newSignatureForm.preparation.stepOf", { step: clock.stepNumber, total: clock.stepCount })}
        {" · "}
        {stepLabel}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={clock.percent}
        aria-label={stepLabel}
        sx={{ my: 0.75, height: 6, borderRadius: 3 }}
      />
      <Typography variant="caption" component="p" color={clock.overdue ? "warning.dark" : "text.primary"}>
        {progress.retrying
          ? t("signatures.newSignatureForm.preparation.busy")
          : readyCount
          ? `${readyCount} · ${timeLeft}`
          : timeLeft}
      </Typography>
      <Typography variant="caption" component="p" color="text.secondary">
        {t("signatures.newSignatureForm.preparation.next", { context })}
      </Typography>
    </Box>
  );
}
